/**
 * Image Gallery Component
 *
 * @package
 */

import { useState, useEffect } from '@wordpress/element';
import PropTypes from 'prop-types';
import { __ } from '@wordpress/i18n';
import { cachedApiFetch } from '../helpers/useApiCache';
import NoBaseModelPlaceholder from './NoBaseModelPlaceholder';

const ImageGallery = ( { config, selectedBaseModel, selectedProducts, pendingBaseModel = false, onLoadingChange = () => {} } ) => {
	// A base model of 0 is the customer explicitly choosing "no base model", as opposed
	// to null, which is simply not having chosen yet.
	const hasNoBaseModel = !! config.allowNoBaseModel && selectedBaseModel === 0;

	const [ images, setImages ] = useState( [] );
	const [ currentImageIndex, setCurrentImageIndex ] = useState( 0 );
	const [ isLoading, setIsLoading ] = useState(
		() => !! ( selectedBaseModel || ( selectedProducts && selectedProducts.length ) || pendingBaseModel )
	);

	// Stable, order-independent dependency: [1,2] and [2,1] reuse one cache entry.
	const productsKey = selectedProducts && selectedProducts.length > 0
		? [ ...selectedProducts ].map( Number ).sort( ( a, b ) => a - b ).join( ',' )
		: '';

	useEffect( () => {
		let ignore = false;

		const showInitialOrEmpty = () => {
			// The initial image is a truck the customer has just declined, so fall through
			// to the placeholder instead of previewing a base model they are not buying.
			if ( hasNoBaseModel ) {
				setImages( [] );
				return;
			}

			if ( config.initialImage ) {
				setImages( [ { url: config.initialImage, type: 'initial' } ] );
				setCurrentImageIndex( 0 );
			} else {
				setImages( [] );
			}
		};

		// A base model from the URL is about to be applied. Hold the loading state
		// rather than flashing the generic initial image before the swap.
		if ( ! selectedBaseModel && ! productsKey && pendingBaseModel ) {
			setIsLoading( true );
			onLoadingChange( true );
			return;
		}

		// Nothing selected: show the initial image without hitting the network.
		if ( ! selectedBaseModel && ! productsKey ) {
			showInitialOrEmpty();
			setIsLoading( false );
			onLoadingChange( false );
			return;
		}

		const params = [];
		if ( selectedBaseModel ) {
			params.push( `base_model=${ selectedBaseModel }` );
		}
		if ( productsKey ) {
			params.push( `products=${ productsKey }` );
		}
		const path = `/at-configurator/v1/images?${ params.join( '&' ) }`;

		setIsLoading( true );
		onLoadingChange( true );

		// `ignore` drops a superseded response so a slow earlier request can't
		// overwrite a newer selection's images.
		cachedApiFetch( path )
			.then( data => {
				if ( ignore ) {
					return;
				}
				if ( Array.isArray( data ) && data.length > 0 ) {
					setImages( data );
					setCurrentImageIndex( 0 );
				} else {
					showInitialOrEmpty();
				}
			} )
			.catch( error => {
				if ( ignore ) {
					return;
				}
				// eslint-disable-next-line no-console
				console.error( 'Failed to fetch images:', error );
				showInitialOrEmpty();
			} )
			.finally( () => {
				if ( ! ignore ) {
					setIsLoading( false );
					onLoadingChange( false );
				}
			} );

		return () => {
			ignore = true;
		};
	}, [ selectedBaseModel, productsKey, config.initialImage, pendingBaseModel, hasNoBaseModel ] );

	if ( images.length === 0 && ! isLoading ) {
		if ( hasNoBaseModel ) {
			return (
				<div className="configurator-gallery" role="group" aria-label={ __( 'Vehicle configuration gallery', 'at-vehicle-configurator' ) }>
					<div className="configurator-gallery-main">
						<NoBaseModelPlaceholder />
					</div>
				</div>
			);
		}

		return (
			<div className="configurator-gallery-empty" role="status" aria-live="polite">
				{ __( 'No images available', 'at-vehicle-configurator' ) }
			</div>
		);
	}

	const getCurrentImageAlt = () => {
		const currentImage = images[ currentImageIndex ];
		if ( currentImage?.alt ) {
			return currentImage.alt;
		}
		return __( 'Vehicle configuration image', 'at-vehicle-configurator' ) + ` ${ currentImageIndex + 1 } ${ __( 'of', 'at-vehicle-configurator' ) } ${ images.length }`;
	};

	const currentImage = images[ currentImageIndex ];

	return (
		<div
			className="configurator-gallery"
			role="group"
			aria-label={ __( 'Vehicle configuration gallery', 'at-vehicle-configurator' ) }
			aria-busy={ isLoading }
		>
			<div className="configurator-gallery-main">
				{ currentImage ? (
					<img
						key={ currentImage.url }
						src={ currentImage.url }
						alt={ getCurrentImageAlt() }
						className="configurator-gallery-image"
						decoding="async"
					/>
				) : (
					<span className="configurator-skeleton configurator-skeleton--image configurator-gallery-image-skeleton" aria-hidden="true" />
				) }
				{ isLoading && <span className="configurator-gallery-loader" aria-hidden="true" /> }
			</div>
			{ images.length > 1 && (
				<div className="configurator-gallery-thumbnails" role="group" aria-label={ __( 'Gallery thumbnails', 'at-vehicle-configurator' ) }>
					{ images.map( ( image, index ) => {
						const thumbnailAlt = image.alt || ( __( 'Thumbnail', 'at-vehicle-configurator' ) + ` ${ index + 1 }` );
						return (
							<button
								key={ index }
								type="button"
								onClick={ () => setCurrentImageIndex( index ) }
								className={ `configurator-gallery-thumbnail ${ currentImageIndex === index ? 'active' : '' }` }
								aria-label={ __( 'View image', 'at-vehicle-configurator' ) + ` ${ index + 1 } ${ __( 'of', 'at-vehicle-configurator' ) } ${ images.length }` }
								aria-current={ currentImageIndex === index ? 'page' : undefined }
							>
								<img src={ image.url } alt={ thumbnailAlt } width={ 80 } height={ 80 } loading="lazy" decoding="async" />
							</button>
						);
					} ) }
				</div>
			) }
		</div>
	);
};

ImageGallery.propTypes = {
	config: PropTypes.object.isRequired,
	selectedBaseModel: PropTypes.oneOfType( [ PropTypes.number, PropTypes.string ] ),
	selectedProducts: PropTypes.array,
	pendingBaseModel: PropTypes.bool,
	onLoadingChange: PropTypes.func,
};

export default ImageGallery;
